// Browser specific logic. Until browser.ts this doesn't need to have the same
// signature as server.ts. Use this for setup functions and other logic that
// only needs to run in the browser. This file should only be imported by
// src/browser.ts or other browser-only files.
// See: docs/isomorphic-imports.md

import { getContext } from 'svelte';
import type { MusicSubscriptionOffer, Me, MediaItem } from '@amp/musickit';
import type { Logger, LoggerFactory } from '@amp/web-apps-logger';
import type { MusicKit, MusicKitGlobal, QueueItem } from '~/utils/musickit';
import { configureMusicKit } from '~/utils/musickit';
import { createPlayerStore } from '~/utils/playback/state';
import type { PlayerStore } from '~/utils/playback/state';
import { createPlaybackQueueStore } from '~/utils/playback/queue';
import type { PlaybackQueueStore } from '~/utils/playback/queue';
import { BaseMusicKitAdapter } from '@amp/web-apps-common';
import type { Jet } from '~/jet';
import { currentTab } from '~/stores/current-tab';
import { get } from 'svelte/store';
import { LIBRARY_TABS, type LibraryItemId, type TabId } from '~/constants/tabs';
import { type PlayAction, makeUserComponentsAction } from '~/jet/models';
import { makeGetTrackMetadataAction } from '~/actions/get-track-metadata-action';

const CONTEXT_NAME = 'playback';
const VOLUME_PREFERENCE_LOCALSTORAGE =
    'music-classical-app-pref:volume-pref-for-user';
const AUTOPLAY_LOCAL_STORAGE_KEY = 'music-classical-app-pref:autoplay';
const FEATURE_NAME_PREFIX = 'MusicKit/AppleClassical';

export type PlayerState = Playback['state'];
export type PlaybackQueue = Playback['queue'];

/**
 * Classical API does not provide us usable URLs which Musickit would recognize
 * So we use a combination of Type -> ID descriptor
 */
type PlaybackByType = {
    album?: number | string;
};

type PlaybackDescriptor =
    | {
          url: string;
          startWith?: string | { type: string; id: string };
          position: string;
      }
    | { uploadedVideo: string; videoContainerElement?: HTMLElement }
    | PlaybackByType;

export function getPlayback(): {
    ready: Promise<void>;
    state: PlayerState;
    queue: PlaybackQueue;
    shouldDisplayPrivacyLink: () => Promise<boolean>;
    isRestricted: () => Promise<boolean>;
    getVolumeAndSubscribeToUpdates: (
        callback?: Function,
    ) => Promise<{ volume: number; removeEventListenerCb: Function }>;
} {
    const playbackInstance = getContext(CONTEXT_NAME) as Playback | undefined;
    if (!playbackInstance) {
        throw new Error('getPlayback called before Playback.load');
    }

    return {
        ready: playbackInstance.ready(), // Allows for blocking of things until MK is ready.
        state: playbackInstance.state,
        queue: playbackInstance.queue,
        shouldDisplayPrivacyLink:
            playbackInstance.shouldDisplayPrivacyLink.bind(playbackInstance),
        isRestricted: playbackInstance.isRestricted.bind(playbackInstance),
        getVolumeAndSubscribeToUpdates:
            playbackInstance.getVolumeAndSubscribeToUpdates.bind(
                playbackInstance,
            ),
    };
}

export class Playback {
    /**
     * Configures a playback instance backed by MusicKit in the given storefront.
     *
     * @param loggerFactory The logger factory to use
     * @param storefront The 2 letter storefront country code of the playback content
     * @returns Playback a promise resolving to the configured Playback instance
     */
    static load(
        context: Map<string, unknown>,
        loggerFactory: LoggerFactory,
        jet: Jet,
        storefront: string,
        language: string,
    ): Playback {
        // TODO: rdar://73364211: if we don't just refresh, support changing storefronts?
        const log = loggerFactory.loggerFor('Playback');
        const initializeMusicKit = async () => {
            try {
                // TODO: rdar://85075106 (Jet Web: Playback Media Items)
                const musicKitPromise = await configureMusicKit(storefront);
                log.info('MusicKit initialized');

                // Need to run MusicKit deferPlayback once on user interaction when MK got initiated to enable playback for DnD
                function setupDeferredPlayback() {
                    musicKitPromise.musicKit.deferPlayback();
                    document.body.removeEventListener(
                        'mousedown',
                        setupDeferredPlayback,
                    );
                }
                document.body.addEventListener(
                    'mousedown',
                    setupDeferredPlayback,
                );

                return musicKitPromise;
            } catch (e) {
                // TODO: rdar://73165545 (Error Handling Across App)
                log.error('error while loading MusicKit:', e);

                // We can't re-throw any errors here. This promise isn't awaited until
                // later (ex. someone calls play()). Because of this, if this promise
                // (the one returned by initializeMusicKit) rejects, that'll be an
                // unhandled promise rejection. So instead we return null, and later
                // when we need to get at this value, we'll turn this into a promise
                // that rejects if null.
                return null;
            }
        };

        const playerInfo = createPlayerStore();
        const playbackQueue = createPlaybackQueueStore();

        const playbackInstance = new Playback(
            log,
            initializeMusicKit(),
            playerInfo,
            playbackQueue,
            jet,
            language,
        );

        context.set(CONTEXT_NAME, playbackInstance);

        // Bind leaving window messaging
        playbackInstance.setupBeforeUnloadHandler();

        // Binding MusicKit Events after the instance has been created
        playbackInstance.bindMusicKitEvents();

        // Set volume preferences
        playbackInstance.checkUserVolumeSettings();

        // Read the playback capabilities after the instance is ready
        playbackInstance.syncPlaybackCapabilities();

        // Set the preview flag for MusicKit
        playbackInstance.setPreviewOnlyBasedOnSF(storefront);

        return playbackInstance;
    }

    private readonly log: Logger;
    private readonly language: string;

    private readonly musicKitAdapter: BaseMusicKitAdapter<
        MusicKit,
        MusicKitGlobal
    >;

    // Global player details
    private readonly stateModifiers: PlayerStore['internal'];
    readonly state: PlayerStore['external'];

    // Currently playing + queue
    private readonly queueModifiers: PlaybackQueueStore['internal'];
    readonly queue: PlaybackQueueStore['external'];

    // Tracks the last time play was called for an async debounce-style call limiting, to help with users
    // double-clicking on items to play instead of single clicking
    private lastPlayTime = 0;

    //shared public methods
    public readonly addEventListener: BaseMusicKitAdapter<
        MusicKit,
        MusicKitGlobal
    >['addEventListener'];
    public readonly addOneTimeEventListener: BaseMusicKitAdapter<
        MusicKit,
        MusicKitGlobal
    >['addOneTimeEventListener'];
    public readonly removeEventListener: BaseMusicKitAdapter<
        MusicKit,
        MusicKitGlobal
    >['removeEventListener'];
    public readonly removeEventListenerWithCb: BaseMusicKitAdapter<
        MusicKit,
        MusicKitGlobal
    >['removeEventListenerWithCb'];
    public readonly ready: BaseMusicKitAdapter<
        MusicKit,
        MusicKitGlobal
    >['ready'];
    public readonly setupBeforeUnloadHandler: BaseMusicKitAdapter<
        MusicKit,
        MusicKitGlobal
    >['setupBeforeUnloadHandler'];

    constructor(
        log: Logger,
        musicKit: Promise<{
            musicKit: MusicKit;
            musicKitGlobal: MusicKitGlobal;
        }>,
        playerStore: PlayerStore,
        playbackQueueStore: PlaybackQueueStore,
        jet: Jet,
        language: string,
    ) {
        this.musicKitAdapter = new BaseMusicKitAdapter(musicKit);

        this.addEventListener = this.musicKitAdapter.addEventListener.bind(
            this.musicKitAdapter,
        );
        this.addOneTimeEventListener =
            this.musicKitAdapter.addOneTimeEventListener.bind(
                this.musicKitAdapter,
            );
        this.removeEventListener =
            this.musicKitAdapter.removeEventListener.bind(this.musicKitAdapter);
        this.removeEventListenerWithCb =
            this.musicKitAdapter.removeEventListenerWithCb.bind(
                this.musicKitAdapter,
            );
        this.ready = this.musicKitAdapter.ready.bind(this.musicKitAdapter);
        this.setupBeforeUnloadHandler =
            this.musicKitAdapter.setupBeforeUnloadHandler.bind(
                this.musicKitAdapter,
            );

        this.log = log;
        this.jet = jet;
        this.language = language;

        // Global player details
        this.stateModifiers = playerStore.internal;
        this.state = playerStore.external;

        // Currently playing + queue
        this.queueModifiers = playbackQueueStore.internal;
        this.queue = playbackQueueStore.external;
    }

    private get musicKit(): Promise<MusicKit> {
        return this.musicKitAdapter.getMusicKit();
    }

    private get musicKitGlobal(): Promise<MusicKitGlobal> {
        return this.musicKitAdapter.getMusicKitGlobal();
    }

    private async bindMusicKitEvents(): Promise<void> {
        try {
            const { Events } = await this.musicKitGlobal;

            await Promise.all([
                this.addEventListener(
                    Events.capabilitiesChanged,
                    this.onCapabilitiesChange.bind(this),
                ),
                this.addEventListener(
                    Events.nowPlayingItemDidChange,
                    this.onNowPlayingItemChange.bind(this),
                ),
                this.addEventListener(
                    Events.timedMetadataDidChange,
                    this.onTimedMetadataChange.bind(this),
                ),
                this.addEventListener(
                    Events.playbackStateDidChange,
                    this.onPlaybackStateChange.bind(this),
                ),
                this.addEventListener(
                    Events.queuePositionDidChange,
                    this.onQueuePositionChange.bind(this),
                ),
                this.addEventListener(
                    Events.queueItemsDidChange,
                    this.onQueueChange.bind(this),
                ),
                this.addEventListener(
                    Events.playbackVolumeDidChange,
                    this.onPlaybackVolumeDidChange.bind(this),
                ),
                this.addEventListener(
                    Events.autoplayEnabledDidChange,
                    this.syncAutoplay.bind(this),
                ),
                this.addEventListener(
                    Events.playbackTimeDidChange,
                    this.onPlaybackTimeDidChange.bind(this),
                ),
                this.addEventListener(
                    Events.repeatModeDidChange,
                    this.onRepeatModeDidChange.bind(this),
                ),
            ]);

            // set initial values
            await Promise.all([
                this.setAutoplayFromLocalStorage(), // This triggers a MK autoplayEnabledDidChange event when updated
                this.onNowPlayingItemChange(),
                this.onPlaybackStateChange(),
            ]);
        } catch (e) {
            // Suppress errors as they'll be unhandled here (since this is run from load)
            this.log.error('Error while binding MusicKit Events', e);
        }
    }

    /**
     * Returns feature name for PAF in format of "tab_name / page_name"
     * @returns string
     */
    getFeatureName(): string {
        const tab = get(currentTab) ?? '';

        if (typeof tab === 'string') {
            if (tab.startsWith('pl.')) {
                return `${FEATURE_NAME_PREFIX}/library/playlist_detail`;
            }
        } else if (LIBRARY_TABS.includes((tab as LibraryItemId).type)) {
            return `${FEATURE_NAME_PREFIX}/library/${tab.type}`;
        }

        return `${FEATURE_NAME_PREFIX}/${(tab as TabId).type}`;
    }

    private async onCapabilitiesChange() {
        this.syncPlaybackCapabilities();
    }

    private async onNowPlayingItemChange() {
        // Get the currentQueueItem instead of using the passed item, because that includes more options like isAutoplay.
        const musickit = await this.musicKit;
        const { queue } = musickit;
        const currentQueueItem = queue?.currentQueueItem;

        if (queue) {
            this.queueModifiers.setNowPlayingItem(currentQueueItem);
        }

        const { item } = currentQueueItem || {};

        // Classical expects the catalog id for querying Presto for information,
        // such as track metadata or user components.
        const id = item?.attributes?.playParams?.catalogId ?? item?.id;

        this.queueModifiers.setNowPlayingItemInfo({
            name: item?.attributes?.name,
            artistName: item?.attributes?.artistName,
            url: item?.attributes?.url || item?.container?.attributes?.url,
            isLibrary: item?.attributes?.playParams?.isLibrary || false,
            streamingItemId: null,
            mediaItemType: currentQueueItem?.item?.type,
            id,
        });

        // If we are not in preview mode and have a catalog id, query for the track
        // menu data to determine in-library/favorites status.
        if (item?.playbackType !== 1 && !!id) {
            const storefront = musickit?.storefrontCountryCode?.toLowerCase();

            if (storefront) {
                await this.jet.perform(
                    makeUserComponentsAction(
                        `/query/tracksMetadata/${storefront}/track/${id}/menu`,
                        'fetch',
                    ),
                );
            }
        }

        this.log.info('Now playing item has changed:', currentQueueItem);
    }

    private async onTimedMetadataChange(timedMetaData: any) {
        const streamingArtworkUrl = timedMetaData?.links.find(
            ({ description }) => description.startsWith('artwork'),
        )?.url;

        const itemInfo = {
            name: timedMetaData.title,
            artistName: timedMetaData.performer,
            url: null, // URL is not known in timedmetadata
            isLibrary: false,
            streamingItemId: timedMetaData?.adamId || null,
            mediaItemType: null,
            streamingArtworkUrl,
        };

        this.queueModifiers.setNowPlayingItemInfo(itemInfo);

        this.log.info('Timed metadata has changed:', itemInfo);
    }

    private async onPlaybackVolumeDidChange() {
        const volume = (await this.musicKit).volume;
        const mut = (await this.musicKit).musicUserToken;
        this.log.info('Playback volume changed:', volume);
        localStorage.setItem(
            `${VOLUME_PREFERENCE_LOCALSTORAGE}-${mut}`,
            `${volume}`,
        );
        this.log.info(
            `Playback volume persisted at: ${VOLUME_PREFERENCE_LOCALSTORAGE} - ${mut}`,
        );
    }

    private async onPlaybackTimeDidChange() {
        const currentPlaybackTime = (await this.musicKit).currentPlaybackTime;
        this.queueModifiers.setCurrentPlaybackTime(currentPlaybackTime);
    }

    private async onPlaybackStateChange() {
        const state = (await this.musicKit).playbackState;
        this.queueModifiers.setPlaybackState(state);
        this.log.info('Playback state has changed:', state);
    }

    private async onQueuePositionChange() {
        const musickit = await this.musicKit;
        const { position } = musickit.queue;

        this.queueModifiers.setNowPlayingItemIndex(position);
        this.log.info('Queue position has changed:', position);
    }

    private async onQueueChange() {
        const musickit = await this.musicKit;

        // Map to get the QueueItem (item + isAutoplay)
        const items = musickit.queue.items.map((_item, index) =>
            musickit.queue.queueItem(index),
        );

        // Get a list of ids for the queue
        // Track metadata queries only work on the catalog id, which we can only get from the play params.
        const queueIds = items.map(
            (item) =>
                item.item?.attributes?.playParams?.catalogId ?? item.item?.id,
        );

        // Fetch Classical-specific track metadata for each track, falling back to
        // the data within the queue item in the event the API doesn't know about it.
        // Initiating here to reduce the amount of time it takes to fetch/transform/store
        // the data and when the UI needs to render that data.  Otherwise there will be a
        // flash of "undefined" values.
        await this.jet.perform(
            makeGetTrackMetadataAction(queueIds, musickit.queue.items),
        );

        this.queueModifiers.setQueueItems(items);
        this.log.info('Queue items have changed:', { items });
    }

    private async onRepeatModeDidChange() {
        const repeatMode = (await this.musicKit).repeatMode;
        this.queueModifiers.setRepeatMode(repeatMode);
        this.log.info('Repeat mode has changed:', repeatMode);
    }

    /**
     * Updates autoplayEnabled: whether the user has enabled autoplay
     * @method syncAutoplay
     * @private
     */
    private async syncAutoplay() {
        try {
            const musickit = await this.musicKit;
            const isAuthorized = await this.isAuthorized();
            const mut = musickit.musicUserToken;

            const autoplayEnabled = isAuthorized && musickit.autoplayEnabled;

            this.queueModifiers.setAutoplayEnabled(autoplayEnabled);
            localStorage.setItem(
                `${AUTOPLAY_LOCAL_STORAGE_KEY}-${mut}`,
                `${autoplayEnabled}`,
            );

            this.log.info('Autoplay state changed:', autoplayEnabled);
        } catch (e) {
            this.log.error('error while syncing autoplayEnagled', e);
            throw new Error('syncAutoplay failed');
        }
    }
    /**
     * Updates autoplayEnabled: gets previous set state from localStorage
     * @method syncAutoplayFromLocalStorage
     * @private
     */
    private async setAutoplayFromLocalStorage() {
        try {
            const musickit = await this.musicKit;
            const isAuthorized = await this.isAuthorized();
            const mut = musickit.musicUserToken;

            const autoplayLocalValue = localStorage.getItem(
                `${AUTOPLAY_LOCAL_STORAGE_KEY}-${mut}`,
            );

            // Enable autoPlay when local value was set to true.
            // By default it will be false on every new MusicKit instance (reload of the web app).
            if (isAuthorized && autoplayLocalValue === 'true') {
                musickit.autoplayEnabled = true;
            }

            this.log.info(
                'Set autoplay from local value:',
                autoplayLocalValue,
                isAuthorized,
            );
        } catch (e) {
            this.log.error('error while syncing autoplay from localStorage', e);
            throw new Error('syncAutoplayFromLocalStorage failed');
        }
    }

    /**
     * @return {boolean} Whether there is a currently signed in user
     */
    async isAuthorized(): Promise<boolean> {
        return (await this.musicKit).isAuthorized;
    }

    // rdar://89034263 (Need Strong Types to be exported for the return Me from .me() method & MusicSubscriptionOffers)
    async musicSubscriptionOffers(): Promise<MusicSubscriptionOffer[]> {
        return (await this.musicKit).musicSubscriptionOffers();
    }

    async isRestricted(): Promise<boolean> {
        return (await this.musicKit).restrictedEnabled;
    }

    async getVolumeAndSubscribeToUpdates(
        callback: (data?: any) => void,
    ): Promise<{ volume: number; removeEventListenerCb: Function }> {
        let removeEventListenerCb;
        const { Events } = await this.musicKitGlobal;
        if (typeof callback === 'function') {
            this.addEventListener(Events.playbackVolumeDidChange, callback);
            removeEventListenerCb = async () => {
                this.removeEventListenerWithCb(
                    Events.playbackVolumeDidChange,
                    callback,
                );
            };
        }

        return {
            volume: (await this.musicKit).volume,
            removeEventListenerCb,
        };
    }

    // rdar://89034263 (Need Strong Types to be exported for the return Me from .me() method & MusicSubscriptionOffers)
    async me(): Promise<Me> {
        return (await this.musicKit).me();
    }

    async unauthorize(): Promise<void> {
        return (await this.musicKit).unauthorize();
    }

    async shouldDisplayPrivacyLink(): Promise<boolean> {
        return (await this.musicKit).shouldDisplayPrivacyLink();
    }

    async updateUserTokenFromStorage(): Promise<void> {
        return (await this.musicKit).updateUserTokenFromStorage();
    }

    async generateEmbedCode(
        url: string,
        options?: { height: string; width: string },
    ): Promise<string> {
        return (await this.musicKitGlobal).generateEmbedCode(url, options);
    }

    async consumerId(): Promise<string | false> {
        return (await this.musicKitGlobal).getInstance().cid;
    }

    /**
     * Immediately play content (and clear any queued items).
     * @param action PlayAction
     * @returns success A promise that rejects if playback fails
     */
    async play(action: PlayAction): Promise<void> {
        if (!action) {
            throw new Error('no play params');
        }

        this.log.info('playing:', action);

        // Actions can contain the playback items referenced as both the containerItem as well as in the items array.
        // Sometimes, the conatainerItem property will be undefined, like an album or playlist on the Home page.
        // Other times, the same object could exist in both places, like a library album or playlist.
        //
        // There are other case where the items array will have specific playback items found in the containerItem,
        // as is the case with a Recording.  The containerItem will be an album, but the items objects will be
        // specific song objects.
        //
        // Consider the action for a container item if only the containerItem object exists OR the containerItem object
        // is the same as the one found in the items array.
        const isContainerItem =
            (action.containerItem?.type && !action.items?.length) ||
            (action.items?.length === 1 &&
                action.items[0].type === action.containerItem?.type);

        const item = isContainerItem
            ? action.containerItem
            : action.items[
                  (action.containerItem?.attributes?.startWith as number) ?? 0
              ];

        let queueParams = {
            context: { featureName: this.getFeatureName() },
            parameters: { l: this.language },
        } as Record<string, any>;

        // If we have a defined array items to be played from the PlayParams (PlayAction), then play them instead of the container.
        // Recording pages are especially affected by this, as an album can be viewed as a collection of recordings, and the Recording
        // page context is displaying just one of these recordings, that have a varying number of tracks.
        if (action.items?.length && !isContainerItem) {
            const playableItemsWithType = action.items?.reduce(
                (param, item) => {
                    if (!param[item.type]) {
                        param[item.type] = [];
                    }

                    if (item?.identifiers?.id) {
                        param[item.type].push(item.identifiers.id);
                    }

                    return param;
                },
                {},
            );

            if (action.containerItem?.type) {
                queueParams.container = {
                    id: action.containerItem?.identifiers?.id,
                    type: action.containerItem?.type,
                };

                if (action.containerItem?.identifiers?.versionHash) {
                    queueParams.container.versionHash =
                        action.containerItem?.identifiers?.versionHash;
                }
            }

            queueParams = {
                ...queueParams,
                ...playableItemsWithType,
            };
        } else {
            queueParams[item?.type] = item?.identifiers?.id;
        }

        if (item?.attributes?.startWith) {
            queueParams.startWith = item.attributes.startWith;
        }

        try {
            const musicKit = await this.musicKit;
            await musicKit.setQueue(queueParams);
            await musicKit.play();
        } catch (e) {
            this.log.error(`error while playing:`, e);
            throw new Error('playback failed');
        }
    }

    /**
     * Pause currently playing item.
     * @returns success A promise that rejects if pause fails
     */
    async pause(): Promise<void> {
        try {
            this.log.info('pause');
            await this.musicKitAdapter.pausePlayback();
        } catch (e) {
            this.log.error(`error while pausing:`, e);
            throw new Error('pause failed');
        }
    }

    /**
     * Resume playback (play without new play data).
     * @returns success A promise that rejects if resume fails
     */
    async resume(): Promise<void> {
        try {
            this.log.info('resume');
            await this.musicKitAdapter.resumePlayback();
        } catch (e) {
            this.log.error(`error while resuming:`, e);
            throw new Error('resume failed');
        }
    }

    /**
     * Change the current playing item to a MediaItem from the queue
     * @param itemId The id from the MediaItem
     * @returns success A promise that rejects if changeToMediaItem from MusicKit Fails
     */
    async changeToQueueItem(itemId: string, itemIndex: number): Promise<void> {
        try {
            const musickit = await this.musicKit;

            if (musickit.queue.item(itemIndex).id !== itemId) {
                throw new Error(
                    'The given item is not at the expected position in queue',
                );
            }

            this.log.info(
                `change current queue item: ${itemId} at position ${itemIndex}`,
            );
            await musickit.changeToMediaAtIndex(itemIndex);
        } catch (e) {
            // TODO: rdar://73165545 (Error Handling Across App)
            this.log.error(
                `error while changing current queue item - ${itemId} at position ${itemIndex}:`,
                e,
            );
            throw new Error('changeToQueueItem failed');
        }
    }

    /**
     * Method to clear all items from the queue
     */
    async clearQueue(): Promise<void> {
        try {
            this.log.info('clear queue');
            (await this.musicKit).clearQueue();
        } catch (e) {
            this.log.error('error while clearing the queue', e);
            throw new Error('clear queue failed');
        }
    }

    /**
     * Method to find the queue item using itemId and then remove it, also remove if it is the nowPlayingItem
     * @param itemId
     */
    async findAndRemoveQueueItems(itemIds: string[]): Promise<void> {
        try {
            const musickit = await this.musicKit;

            // If one of the removed itemIds was now Playing Item then change the current
            // playing item to the first element of current queue
            if (itemIds?.includes(musickit.queue.currentItem?.id)) {
                const currentItemIndex = musickit.queue.items.findIndex(
                    (mediaItem: MediaItem) =>
                        mediaItem?.id === musickit.queue.currentItem?.id,
                );
                musickit.changeToMediaAtIndex(currentItemIndex);
            }

            // Only remove if the itemId matches, using the filter function passed to below method as per
            // https://github.pie.apple.com/media-js/mediakit-js/blame/537a47a1abd4fc83dc8fd30fe812304b0d2dcf00/packages/musickit/src/queue/index.ts#L257
            musickit.queue.removeQueueItems((queueItem: QueueItem) => {
                const itemFound = itemIds.includes(queueItem?.item?.id);

                return itemFound;
            });
        } catch (e) {
            this.log.error(
                `error while finding and removing the queue items:  ${itemIds}`,
                e,
            );
            throw new Error('findAndRemoveQueueItem failed');
        }
    }

    /**
     * Method to remove a single item from the queue
     */
    async removeQueueItem(itemId: string, itemIndex: number): Promise<void> {
        try {
            const musickit = await this.musicKit;

            if (musickit.queue.item(itemIndex).id !== itemId) {
                throw new Error(
                    'The given item is not at the expected position in queue',
                );
            }

            this.log.info(
                `remove item from queue: ${itemId} at position ${itemIndex}`,
            );
            musickit.queue.remove(itemIndex);
        } catch (e) {
            this.log.error(
                `error while removing item from the queue - ${itemId} at position ${itemIndex}:`,
                e,
            );
            throw new Error('removing item from the queue failed');
        }
    }

    /**
     * checks the user preferences for the volume settings
     */
    async checkUserVolumeSettings() {
        try {
            const musickit = await this.musicKit;
            const mut = musickit.musicUserToken;
            const volumePreference = localStorage.getItem(
                `${VOLUME_PREFERENCE_LOCALSTORAGE}-${mut}`,
            );

            let volume;
            if (volumePreference) {
                volume = parseFloat(volumePreference);
            } else {
                volume = 0.5;
            }
            musickit.volume = volume;
        } catch (e) {
            this.log.warn('error while adjusting volume', e);
        }
    }

    /**
     * synchronize the playback capabilities from the MusicKit instance.
     */
    async syncPlaybackCapabilities() {
        try {
            const musickit = await this.musicKit;
            const mkCapabilities = {
                canEditPlaybackQueue:
                    musickit.capabilities.canEditPlaybackQueue,
            };

            this.stateModifiers.setPlaybackCapabilities(mkCapabilities);
            this.log.info(
                'Playback Capabilities state has been updated',
                mkCapabilities,
            );
        } catch (e) {
            this.log.warn('error while syncing capabilities', e);
        }
    }

    /**
     * method to set previewOnly flag appropriately on Musickit
     */
    async setPreviewOnlyBasedOnSF(visitingStorefront: string) {
        try {
            const musickit = await this.musicKit;
            const isInUserStorefront =
                visitingStorefront?.toLowerCase() ===
                musickit?.storefrontCountryCode?.toLowerCase();
            musickit.previewOnly = !isInUserStorefront;
        } catch (e) {
            // TODO: rdar://73165545 (Error Handling Across App)
            this.log.error(`error while setting previewOnly flag`, e);
        }
    }
}
